'use client';

import React, { useEffect, useState, createContext, useContext } from 'react';
import Image from 'next/image';

// ─── Theme Context ────────────────────────────────────────────────────────────
const ThemeContext = createContext<{
  dark: boolean;
  toggle: () => void;
  nameHovered: boolean;
  setNameHovered: (v: boolean) => void;
}>({
  dark: false,
  toggle: () => {},
  nameHovered: false,
  setNameHovered: () => {},
});

function useTheme() {
  return useContext(ThemeContext);
}

// ─── Types ───────────────────────────────────────────────────────────────────
interface Project {
  id: number;
  emoji: string;
  emojiLabel: string;
  title: string;
  description: string;
  tags: { label: string; bg: string; color: string }[];
  link: string;
  cardBg: string;
  image?: string;
  accentColor?: string;
  highlights?: string[];
}

interface Experience {
  title: string;
  company: string;
  duration: string;
  points: string[];
  certificateLink?: string;
  icon?: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const PROJECTS: Project[] = [
  {
    id: 1,
    emoji: '🛡️',
    emojiLabel: 'shield',
    title: 'wazuh siem home soc lab',
    description:
      'deployed a personal soc lab on ubuntu server utilizing wazuh siem and sysmon-monitored windows endpoints for centralized log collection and threat detection.',
    tags: [
      { label: 'Wazuh SIEM', bg: 'rgba(220,38,38,0.10)', color: '#DC2626' },
      { label: 'Ubuntu Server', bg: 'rgba(153,50,250,0.10)', color: '#7c3aed' },
      { label: 'Threat Detection', bg: 'rgba(38,156,48,0.10)', color: '#166534' },
      { label: 'MITRE ATT&CK', bg: 'rgba(36,145,255,0.10)', color: '#1d4ed8' },
      { label: 'Incident Response', bg: 'rgba(255,116,36,0.10)', color: '#c2410c' },
    ],
    link: 'https://github.com/riteshvijaykumar/soc-homelab',
    cardBg: 'rgb(233, 234, 244)',
    image: '/assets/images/wazuh-dashboard.png',
    accentColor: '#DC2626',
    highlights: ['Real-time threat detection', 'MITRE ATT&CK mapping', 'Custom detection rules'],
  },
  {
    id: 2,
    emoji: '🔬',
    emojiLabel: 'microscope',
    title: 'malware analysis automation framework',
    description:
      'python framework that streamlines malware analysis by combining static and dynamic tooling, automation, and sandboxing to speed up triage, unpacking, and reproducible behavioural analysis.',
    tags: [
      { label: 'Python', bg: 'rgba(36,145,255,0.10)', color: '#1d4ed8' },
      { label: 'YARA', bg: 'rgba(255,36,229,0.10)', color: '#86198f' },
      { label: 'PEfile', bg: 'rgba(38,156,48,0.10)', color: '#166534' },
      { label: 'Static Analysis', bg: 'rgba(255,116,36,0.10)', color: '#c2410c' },
      { label: 'Automation', bg: 'rgba(220,38,38,0.10)', color: '#DC2626' },
    ],
    link: 'https://github.com/riteshvijaykumar/Malware-Reverse-Engineering-Framework',
    cardBg: 'rgba(150, 180, 224, 0.18)',
    image: '/assets/images/Malware-project.png',
    accentColor: '#7c3aed',
    highlights: ['Static & dynamic analysis', 'YARA rule integration', 'Automated triage pipeline'],
  },
];

const EXPERIENCE: Experience[] = [
  {
    title: 'Virtual Internship — AI on Microsoft Azure',
    company: 'Microsoft Azure',
    duration: '4 weeks',
    certificateLink:
      'https://drive.google.com/file/d/1LjvMJbK-6OBu1vMjP7_SZ1tGXT-YrPxS/view?usp=sharing',
    icon: '☁️',
    points: [
      'Completed a 4-week Microsoft Azure AI internship with practical exposure to cloud technologies and secure deployment practices.',
      'Gained foundational knowledge of Azure cloud architecture, identity and access management (IAM), cloud security principles, and secure data handling.',
      'Developed an understanding of cloud governance, access control, and security best practices for building secure and scalable cloud environments.',
    ],
  },
];

// ─── Certifications Data ─────────────────────────────────────────────────────
interface Certification {
  id: number;
  title: string;
  issuer: string;
  issuerIcon: string;
  driveLink: string;
  driveFileId: string;
  image: string;
  accentColor: string;
  bgColor: string;
  tags: string[];
}

const CERTIFICATIONS: Certification[] = [
  {
    id: 1,
    title: 'Essentials of Cloud Computing',
    issuer: 'Infosys Springboard',
    issuerIcon: '🏢',
    driveLink: 'https://drive.google.com/file/d/1LxQahGAklDDMzzqml7I1w_psF9nVyJ3R/view?usp=sharing',
    driveFileId: '1LxQahGAklDDMzzqml7I1w_psF9nVyJ3R',
    image: '/assets/images/cert-cloud.png',
    accentColor: '#0066cc',
    bgColor: 'rgba(0,102,204,0.08)',
    tags: ['Cloud Computing', 'Infrastructure', 'Infosys'],
  },
  {
    id: 2,
    title: 'Privacy and Security in Online Social Media',
    issuer: 'NPTEL — IIT Madras',
    issuerIcon: '🎓',
    driveLink: 'https://drive.google.com/file/d/16vp2l7_ZzQCez8lEuT3cPmYiA6tSkq2D/view?usp=sharing',
    driveFileId: '16vp2l7_ZzQCez8lEuT3cPmYiA6tSkq2D',
    image: '/assets/images/cert-privacy.png',
    accentColor: '#dc2626',
    bgColor: 'rgba(220,38,38,0.08)',
    tags: ['Privacy', 'Cybersecurity', 'Social Media', 'NPTEL'],
  },
  {
    id: 3,
    title: 'Career-Ready Cybersecurity Project Workshop',
    issuer: 'K7 Academy',
    issuerIcon: '🛡️',
    driveLink: 'https://drive.google.com/file/d/19dfjpHfBm6zfPre0jp96KomGYb-bzRdZ/view?usp=sharing',
    driveFileId: '19dfjpHfBm6zfPre0jp96KomGYb-bzRdZ',
    image: '/assets/images/cert-k7.png',
    accentColor: '#7c3aed',
    bgColor: 'rgba(124,58,237,0.08)',
    tags: ['Cybersecurity', 'Hands-on', 'Workshop', 'K7'],
  },
];

const SKILLS = [
  { label: 'SOC Operations', bg: 'rgba(220,38,38,0.10)', color: '#DC2626', icon: '🔴' },
  { label: 'Threat Detection', bg: 'rgba(36,145,255,0.10)', color: '#1d4ed8', icon: '🔵' },
  { label: 'Incident Response', bg: 'rgba(38,156,48,0.10)', color: '#166534', icon: '🟢' },
  { label: 'Digital Forensics', bg: 'rgba(153,50,250,0.10)', color: '#7c3aed', icon: '🟣' },
  { label: 'Wazuh SIEM', bg: 'rgba(255,116,36,0.10)', color: '#c2410c', icon: '🟠' },
  { label: 'MITRE ATT&CK', bg: 'rgba(255,36,229,0.10)', color: '#86198f', icon: '🔮' },
  { label: 'Python', bg: 'rgba(36,145,255,0.10)', color: '#1d4ed8', icon: '🐍' },
  { label: 'Log Analysis', bg: 'rgba(38,156,48,0.10)', color: '#166534', icon: '📊' },
  { label: 'Network Security', bg: 'rgba(220,38,38,0.10)', color: '#DC2626', icon: '🌐' },
  { label: 'Malware Analysis', bg: 'rgba(153,50,250,0.10)', color: '#7c3aed', icon: '🦠' },
  { label: 'Azure Cloud', bg: 'rgba(36,145,255,0.10)', color: '#1d4ed8', icon: '☁️' },
  { label: 'Linux', bg: 'rgba(255,116,36,0.10)', color: '#c2410c', icon: '🐧' },
];

// ─── Scroll Animation Hook ────────────────────────────────────────────────────
function useScrollAnimation() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

// ─── Tag Component ────────────────────────────────────────────────────────────
function Tag({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span className="tag-pill text-xs font-medium" style={{ backgroundColor: bg, color }}>
      {label}
    </span>
  );
}

// ─── Theme Toggle Icon ────────────────────────────────────────────────────────
function ThemeToggle() {
  const { dark, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="theme-toggle-btn"
      title={dark ? 'Light mode' : 'Dark mode'}
    >
      {dark ? (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

// ─── Nav Component ────────────────────────────────────────────────────────────
function Nav() {
  const { dark, nameHovered } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'projects', href: '#projects' },
    { label: 'about', href: '#about' },
    { label: 'experience', href: '#experience' },
    { label: 'certifications', href: '#certifications' },
    { label: 'achievements', href: '#achievements' },
    { label: 'events', href: '#events' },
    { label: 'contact', href: '#contact' },
  ];

  return (
    <>
      <header
        className="fixed top-5 left-0 right-0 z-50 flex justify-center pointer-events-none transition-opacity duration-400"
        style={{
          opacity: nameHovered ? 0.25 : 1,
          transition: 'opacity 0.4s ease',
        }}
      >
        <div
          className={`pointer-events-auto flex items-center gap-1 px-3 py-2 rounded-full transition-all duration-300 ${
            dark
              ? 'bg-[rgba(15,15,15,0.85)] border border-white/10'
              : 'bg-[rgba(255,255,255,0.85)] border border-black/8'
          } ${
            scrolled
              ? 'shadow-[0_8px_32px_rgba(0,0,0,0.18)] backdrop-blur-xl'
              : 'shadow-[0_4px_20px_rgba(0,0,0,0.10)] backdrop-blur-md'
          }`}
        >
          <a
            href="#"
            className="font-mono text-sm font-semibold tracking-tight px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors"
            style={{ color: dark ? '#f1f1f1' : '#111111' }}
          >
            rv<span className="asterisk-accent">*</span>
          </a>

          <span
            className="w-px h-4 mx-1"
            style={{ background: dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)' }}
          />

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-mono text-xs px-3 py-1.5 rounded-full transition-all duration-200 hover:bg-[rgba(220,38,38,0.1)] hover:text-[#dc2626]"
                style={{ color: dark ? 'rgba(241,241,241,0.75)' : 'rgba(17,17,17,0.7)' }}
              >
                [{link.label}]
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-1 ml-1">
            <a
              href="https://drive.google.com/file/d/1YuOhNaSwPk2Yk2k47PNygP4Z1yO4kYnW/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs px-3 py-1.5 rounded-full transition-all duration-200 hover:bg-[rgba(220,38,38,0.1)] hover:text-[#dc2626]"
              style={{ color: dark ? 'rgba(241,241,241,0.75)' : 'rgba(17,17,17,0.7)' }}
            >
              [resume]
            </a>
            <a
              href="mailto:v.riteshvijaykumar@gmail.com"
              className="font-mono text-xs px-3 py-1.5 rounded-full transition-all duration-200"
              style={{ color: '#DC2626', fontWeight: 600 }}
            >
              [connect!]
            </a>
            <ThemeToggle />
          </div>

          <div className="md:hidden flex items-center gap-2 ml-1">
            <ThemeToggle />
            <button
              className="flex flex-col gap-1 p-1.5 rounded-full hover:bg-black/5 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span
                className={`block w-4 h-0.5 transition-transform duration-200 ${dark ? 'bg-gray-200' : 'bg-gray-700'} ${menuOpen ? 'rotate-45 translate-y-1.5' : ''}`}
              />
              <span
                className={`block w-4 h-0.5 transition-opacity duration-200 ${dark ? 'bg-gray-200' : 'bg-gray-700'} ${menuOpen ? 'opacity-0' : ''}`}
              />
              <span
                className={`block w-4 h-0.5 transition-transform duration-200 ${dark ? 'bg-gray-200' : 'bg-gray-700'} ${menuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}
              />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          className={`fixed top-[72px] left-1/2 -translate-x-1/2 z-40 w-56 rounded-2xl px-4 py-4 flex flex-col gap-3 shadow-xl ${
            dark
              ? 'bg-[rgba(15,15,15,0.95)] border border-white/10 backdrop-blur-xl'
              : 'bg-[rgba(255,255,255,0.95)] border border-black/8 backdrop-blur-xl'
          }`}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="font-mono text-sm transition-colors hover:text-[#dc2626]"
              style={{ color: dark ? 'rgba(241,241,241,0.8)' : 'rgba(17,17,17,0.8)' }}
              onClick={() => setMenuOpen(false)}
            >
              [{link.label}]
            </a>
          ))}
          <a
            href="https://drive.google.com/file/d/1YuOhNaSwPk2Yk2k47PNygP4Z1yO4kYnW/view?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm transition-colors hover:text-[#dc2626]"
            style={{ color: dark ? 'rgba(241,241,241,0.8)' : 'rgba(17,17,17,0.8)' }}
            onClick={() => setMenuOpen(false)}
          >
            [resume]
          </a>
          <a
            href="mailto:v.riteshvijaykumar@gmail.com"
            className="font-mono text-sm font-semibold"
            style={{ color: '#DC2626' }}
            onClick={() => setMenuOpen(false)}
          >
            [connect!]
          </a>
        </div>
      )}
    </>
  );
}

// ─── Hanging ID Card Component ────────────────────────────────────────────────
function HangingIDCard() {
  return (
    <div className="id-card-wrapper" aria-hidden="true">
      <div className="lanyard-container">
        <div className="lanyard-rope" />
        <div className="id-card">
          <div className="card-hole-row">
            <div className="card-hole" />
          </div>
          <div className="card-body">
            <div className="card-photo-wrap">
              <Image
                src="/assets/images/ritesh_edited.png"
                alt="Ritesh V ID card photo"
                width={300}
                height={300}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="card-info">
              <p className="card-name">Ritesh V</p>
              <p className="card-role">Cyber Security Analyst</p>
              <div className="card-badge">
                <span className="card-badge-dot" />
                <span className="card-badge-text">OPEN TO INTERNSHIPS</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Hero Section ─────────────────────────────────────────────────────────────
function HeroSection() {
  const { dark, nameHovered, setNameHovered } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const lineStyle = (delayMs: number, dimOnHover = true) => ({
    opacity: !mounted ? 0 : nameHovered && dimOnHover ? 0.08 : 1,
    transform: !mounted ? 'translateY(32px)' : 'translateY(0)',
    transition: `opacity 0.4s ease, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delayMs}ms`,
  });
  return (
    <section
      className={`relative min-h-screen flex items-center pt-20 ${dark ? 'dot-pattern-dark' : 'dot-pattern'}`}
      id="home"
    >
      <div className="max-w-5xl mx-auto px-8 w-full py-16">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-12 lg:gap-16">
          {/* Left: Text */}
          <div className="flex-1 min-w-0">
            {/* hi, i'm */}
            <div className="mb-2" style={lineStyle(100)}>
              <p
                className={`hero-title ${dark ? 'hero-title-dark' : ''}`}
                style={{ fontSize: 'clamp(40px, 7vw, 86px)' }}
              >
                hi, i&apos;m
              </p>
            </div>

            {/* Ritesh V — brightens, scales, glows on hover */}
            <div className="mb-2" style={lineStyle(220, false)}>
              <p
                className={`hero-title ${dark ? 'hero-title-dark' : ''}`}
                style={{
                  fontSize: 'clamp(40px, 7vw, 86px)',
                  cursor: 'pointer',
                  display: 'inline-block',
                  transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), filter 0.4s ease',
                  transform: nameHovered ? 'scale(1.06)' : 'scale(1)',
                  filter: nameHovered
                    ? dark
                      ? 'brightness(1.4) drop-shadow(0 0 24px rgba(220,38,38,0.55)) drop-shadow(0 0 8px rgba(220,38,38,0.3))'
                      : 'brightness(1.15) drop-shadow(0 0 20px rgba(220,38,38,0.45)) drop-shadow(0 0 6px rgba(220,38,38,0.25))'
                    : 'none',
                  transformOrigin: 'left center',
                }}
                onMouseEnter={() => setNameHovered(true)}
                onMouseLeave={() => setNameHovered(false)}
              >
                Ritesh V
              </p>
            </div>

            {/* and i defend! */}
            <div style={lineStyle(340)}>
              <p
                className={`hero-title mb-2 ${dark ? 'hero-title-dark' : ''}`}
                style={{ fontSize: 'clamp(40px, 7vw, 86px)' }}
              >
                and i <span className="hero-italic">defend</span>!
              </p>
            </div>

            {/* Slogan */}
            <div
              className="flex items-start gap-2 mb-6 mt-1"
              style={lineStyle(460)}
            >
              <span className="asterisk-accent font-serif text-lg mt-0.5">*</span>
              <p
                className="text-sm"
                style={{
                  color: dark ? 'rgba(240,240,245,0.75)' : 'rgba(17,17,17,0.75)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                }}
              >
                Think like an attacker. Build like a defender.
              </p>
            </div>

            {/* Description — visible only on hover */}
            <p
              className="text-xl sm:text-2xl mb-8 max-w-lg"
              style={{
                color: dark ? 'rgba(240,240,245,0.85)' : 'rgba(17,17,17,0.85)',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                lineHeight: '1.5',
                opacity: nameHovered ? 1 : 0,
                transform: nameHovered ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                pointerEvents: nameHovered ? 'auto' : 'none',
              }}
            >
              A Cyber Security Engineering student crafting
              <br />
              defenses that protect systems and detect threats.
            </p>

            {/* Contact / social */}
            <div
              className="flex flex-col sm:flex-row sm:items-center gap-6"
              style={lineStyle(580)}
            >
              <div>
                <p
                  className="text-sm mb-1"
                  style={{
                    color: dark ? 'rgba(240,240,245,0.45)' : 'rgba(17,17,17,0.45)',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Reach me at
                </p>
                <a
                  href="mailto:v.riteshvijaykumar@gmail.com"
                  className="text-lg sm:text-xl font-medium hover:text-red-600 transition-colors duration-200"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    color: dark ? '#f0f0f5' : '#111111',
                  }}
                >
                  v.riteshvijaykumar@gmail.com
                </a>
              </div>

              <div className="flex items-center gap-3">
                <a
                  href="https://linkedin.com/in/ritesh-v-/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`social-icon-btn ${dark ? 'social-icon-btn-dark' : ''}`}
                  aria-label="LinkedIn"
                >
                  <LinkedInIcon />
                </a>
                <a
                  href="https://github.com/riteshvijaykumar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`social-icon-btn ${dark ? 'social-icon-btn-dark' : ''}`}
                  aria-label="GitHub"
                >
                  <GitHubIcon />
                </a>
                <a
                  href="mailto:v.riteshvijaykumar@gmail.com"
                  className={`social-icon-btn ${dark ? 'social-icon-btn-dark' : ''}`}
                  aria-label="Email"
                >
                  <EmailIcon />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Hanging ID Card */}
          <div
            className="flex-shrink-0 flex flex-col items-center"
            style={{
              opacity: !mounted ? 0 : 1,
              transform: !mounted
                ? 'translateY(32px)'
                : nameHovered
                ? 'scale(1.08) translateY(-8px)'
                : 'scale(1) translateY(0)',
              filter: nameHovered
                ? dark
                  ? 'brightness(1.25) drop-shadow(0 16px 40px rgba(220,38,38,0.35)) drop-shadow(0 4px 16px rgba(0,0,0,0.4))'
                  : 'brightness(1.1) drop-shadow(0 16px 40px rgba(220,38,38,0.28)) drop-shadow(0 4px 16px rgba(0,0,0,0.18))'
                : 'none',
              transition: 'opacity 0.7s ease 350ms, transform 0.5s cubic-bezier(0.34,1.56,0.64,1), filter 0.4s ease',
            }}
          >
            <HangingIDCard />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Project Card Component ───────────────────────────────────────────────────
function ProjectCard({ project, idx }: { project: Project; idx: number }) {
  const { dark } = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="project-card-enhanced animate-on-scroll rounded-3xl overflow-hidden"
      style={{ animationDelay: `${idx * 0.15}s` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image Section */}
      {project.image && (
        <div className="relative w-full overflow-hidden" style={{ height: '240px' }}>
          <Image
            src={project.image}
            alt={`${project.title} screenshot`}
            width={600}
            height={300}
            className="w-full h-full object-cover object-top transition-transform duration-700"
            style={{
              filter: dark ? 'brightness(0.8)' : 'none',
              transform: hovered ? 'scale(1.05)' : 'scale(1)',
            }}
          />
          {/* Gradient overlay */}
          <div
            className="absolute inset-0"
            style={{
              background: dark
                ? 'linear-gradient(to bottom, transparent 40%, rgba(15,15,15,0.85) 100%)'
                : 'linear-gradient(to bottom, transparent 40%, rgba(255,255,255,0.9) 100%)',
            }}
          />
          {/* Live badge */}
          <div className="absolute top-4 right-4">
            <span
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md"
              style={{
                background: 'rgba(0,0,0,0.55)',
                color: '#ffffff',
                fontFamily: 'Space Mono, monospace',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              view project
            </span>
          </div>
          {/* Project number */}
          <div className="absolute top-4 left-4">
            <span
              className="font-mono text-xs px-2.5 py-1 rounded-full"
              style={{
                background: project.accentColor
                  ? `${project.accentColor}22`
                  : 'rgba(220,38,38,0.2)',
                color: project.accentColor || '#DC2626',
                border: `1px solid ${project.accentColor || '#DC2626'}44`,
                backdropFilter: 'blur(8px)',
              }}
            >
              0{project.id}
            </span>
          </div>
        </div>
      )}

      {/* No image fallback */}
      {!project.image && (
        <div
          className="w-full flex items-center justify-center"
          style={{
            height: '120px',
            background: dark ? 'rgba(255,255,255,0.04)' : project.cardBg,
            fontSize: '64px',
          }}
        >
          <span
            role="img"
            aria-label={project.emojiLabel}
            className="transition-transform duration-300"
            style={{ transform: hovered ? 'scale(1.2) rotate(10deg)' : 'scale(1)' }}
          >
            {project.emoji}
          </span>
        </div>
      )}

      {/* Content */}
      <div
        className="p-6 sm:p-8"
        style={{
          background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.95)',
          borderTop: dark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(17,17,17,0.06)',
        }}
      >
        {/* Title row */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            {project.image && (
              <span role="img" aria-label={project.emojiLabel} className="text-xl">
                {project.emoji}
              </span>
            )}
            <h3
              className="text-xl sm:text-2xl font-bold"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 700,
                color: dark ? '#f1f1f1' : '#111111',
                lineHeight: '1.3',
              }}
            >
              {project.title}
            </h3>
          </div>
          <span
            className="flex-shrink-0 project-link-btn"
            style={{
              color: project.accentColor || '#DC2626',
            }}
            aria-label={`View ${project.title} on GitHub`}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </span>
        </div>

        {/* Description */}
        <p
          className="text-base sm:text-lg mb-4"
          style={{
            color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            lineHeight: '1.6',
          }}
        >
          {project.description}
        </p>

        {/* Highlights */}
        {project.highlights && (
          <div className="flex flex-wrap gap-2 mb-4">
            {project.highlights.map((h) => (
              <span
                key={h}
                className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full"
                style={{
                  background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(17,17,17,0.04)',
                  color: dark ? 'rgba(241,241,241,0.6)' : 'rgba(17,17,17,0.6)',
                  fontFamily: 'Space Mono, monospace',
                  border: dark
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(17,17,17,0.08)',
                }}
              >
                <span style={{ color: project.accentColor || '#DC2626' }}>✓</span> {h}
              </span>
            ))}
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag) => (
            <Tag key={tag.label} {...tag} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Projects Section ─────────────────────────────────────────────────────────
function ProjectsSection() {
  const { dark } = useTheme();
  return (
    <section id="projects" className="py-24 px-8">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="mb-14 animate-on-scroll">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="font-mono text-xs px-3 py-1 rounded-full"
              style={{
                background: 'rgba(220,38,38,0.1)',
                color: '#DC2626',
                border: '1px solid rgba(220,38,38,0.2)',
              }}
            >
              ◆ portfolio
            </span>
          </div>
          <p
            className={`section-title text-3xl sm:text-4xl mb-2 ${dark ? 'section-title-dark' : ''}`}
          >
            Featured Projects
          </p>
          <p
            className="text-xl sm:text-2xl"
            style={{
              color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
            }}
          >
            My craft, condensed into the tools I am proud of!
          </p>
        </div>

        {/* Project Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PROJECTS.map((project, idx) => (
            <a
              key={project.id}
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              <ProjectCard project={project} idx={idx} />
            </a>
          ))}
        </div>

        {/* More on GitHub */}
        <div className="mt-12 text-center animate-on-scroll">
          <p
            className="text-lg mb-4"
            style={{
              color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Want to see more?
          </p>
          <a
            href="https://github.com/riteshvijaykumar"
            target="_blank"
            rel="noopener noreferrer"
            className="github-cta-btn inline-flex items-center gap-2"
          >
            <GitHubIcon size={16} />
            check github here
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── About Section ────────────────────────────────────────────────────────────
function AboutSection() {
  const { dark } = useTheme();
  const [activeSkill, setActiveSkill] = useState<string | null>(null);

  return (
    <section id="about" className={`py-24 px-8 ${dark ? 'dot-pattern-dark' : 'dot-pattern'}`}>
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left */}
          <div className="animate-on-scroll">
            <div className="flex items-center gap-3 mb-3">
              <span
                className="font-mono text-xs px-3 py-1 rounded-full"
                style={{
                  background: 'rgba(220,38,38,0.1)',
                  color: '#DC2626',
                  border: '1px solid rgba(220,38,38,0.2)',
                }}
              >
                ◆ about
              </span>
            </div>
            <p
              className={`section-title text-3xl sm:text-4xl mb-6 ${dark ? 'section-title-dark' : ''}`}
            >
              About Me
            </p>
            <div
              className="space-y-4 text-lg"
              style={{
                color: dark ? 'rgba(241,241,241,0.75)' : 'rgba(17,17,17,0.75)',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                lineHeight: '1.6',
              }}
            >
              <p>
                I&apos;m <strong style={{ color: dark ? '#f1f1f1' : '#111111' }}>Ritesh</strong>, a
                Cyber Security Engineering student with a passion for defending systems,
                investigating threats, and building practical security solutions.
              </p>
              <p>
                My primary interests lie in{' '}
                <strong style={{ color: '#DC2626' }}>Security Operations (SOC)</strong>,{' '}
                <strong style={{ color: '#DC2626' }}>Threat Detection</strong>,{' '}
                <strong style={{ color: '#DC2626' }}>Incident Response</strong>, and{' '}
                <strong style={{ color: '#DC2626' }}>Digital Forensics</strong>.
              </p>
              <p>
                I spend much of my time building home lab environments, configuring SIEM platforms
                such as Wazuh, analyzing logs, exploring attack techniques, and developing detection
                and response workflows.
              </p>
              <p>
                I believe the best way to learn cybersecurity is by building, breaking, and
                understanding how systems behave under real-world conditions.
              </p>
              <p className="font-semibold" style={{ color: '#DC2626' }}>
                I&apos;m currently seeking internship opportunities where I can apply my skills,
                collaborate with experienced professionals, and continue growing in SOC operations,
                threat detection, and incident response.
              </p>
            </div>

            {/* Stats Cards */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { value: '2+', label: 'Security Projects', icon: '🛡️' },
                { value: '1', label: 'Cloud Internship', icon: '☁️' },
                { value: 'SOC', label: 'Primary Focus', icon: '🔍' },
                { value: '∞', label: 'Curiosity', icon: '🧠' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="stat-card p-5 rounded-2xl flex flex-col gap-1"
                  style={{
                    background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
                    border: dark
                      ? '1px solid rgba(255,255,255,0.1)'
                      : '1px solid rgba(17,17,17,0.08)',
                    boxShadow: dark ? '0 4px 24px rgba(0,0,0,0.2)' : '0 4px 24px rgba(0,0,0,0.06)',
                  }}
                >
                  <span className="text-2xl mb-1">{stat.icon}</span>
                  <p
                    className="text-3xl font-bold"
                    style={{ color: '#DC2626', fontFamily: 'Outfit, sans-serif' }}
                  >
                    {stat.value}
                  </p>
                  <p
                    className="text-sm"
                    style={{
                      color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Skills */}
          <div className="animate-on-scroll">
            <p className={`section-title text-2xl mb-2 ${dark ? 'section-title-dark' : ''}`}>
              Skills & Tools
            </p>
            <p
              className="text-sm mb-6"
              style={{
                color: dark ? 'rgba(241,241,241,0.45)' : 'rgba(17,17,17,0.45)',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Hover over a skill to highlight it
            </p>
            <div className="flex flex-wrap gap-2">
              {SKILLS.map((skill) => (
                <button
                  key={skill.label}
                  className="skill-pill-btn"
                  onMouseEnter={() => setActiveSkill(skill.label)}
                  onMouseLeave={() => setActiveSkill(null)}
                  style={{
                    backgroundColor:
                      activeSkill === skill.label
                        ? skill.bg
                        : dark
                          ? 'rgba(255,255,255,0.05)'
                          : 'rgba(17,17,17,0.04)',
                    color:
                      activeSkill === skill.label
                        ? skill.color
                        : dark
                          ? 'rgba(241,241,241,0.65)'
                          : 'rgba(17,17,17,0.65)',
                    border: `1px solid ${activeSkill === skill.label ? skill.color + '44' : dark ? 'rgba(255,255,255,0.1)' : 'rgba(17,17,17,0.1)'}`,
                    transform:
                      activeSkill === skill.label
                        ? 'translateY(-2px) scale(1.05)'
                        : 'translateY(0) scale(1)',
                    boxShadow: activeSkill === skill.label ? `0 4px 16px ${skill.color}22` : 'none',
                  }}
                >
                  <span className="mr-1.5">{skill.icon}</span>
                  {skill.label}
                </button>
              ))}
            </div>

            {/* Active skill detail */}
            <div
              className="mt-6 p-4 rounded-2xl transition-all duration-300"
              style={{
                background: activeSkill
                  ? dark
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(17,17,17,0.03)'
                  : 'transparent',
                border: activeSkill
                  ? dark
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(17,17,17,0.06)'
                  : '1px solid transparent',
                opacity: activeSkill ? 1 : 0,
                transform: activeSkill ? 'translateY(0)' : 'translateY(8px)',
              }}
            >
              {activeSkill && (
                <>
                  <p
                    className="text-sm font-medium"
                    style={{
                      color: dark ? 'rgba(241,241,241,0.85)' : 'rgba(17,17,17,0.85)',
                      fontFamily: 'Space Mono, monospace',
                    }}
                  >
                    {SKILLS.find((s) => s.label === activeSkill)?.icon} {activeSkill}
                  </p>
                  <p
                    className="text-xs mt-1"
                    style={{
                      color: dark ? 'rgba(241,241,241,0.45)' : 'rgba(17,17,17,0.45)',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    core competency · actively applied in projects
                  </p>
                </>
              )}
            </div>

            {/* Cybersecurity Terminal Card */}
            <div
              className="mt-6 p-5 rounded-2xl font-mono text-sm"
              style={{
                background: dark ? 'rgba(0,0,0,0.5)' : 'rgba(17,17,17,0.96)',
                border: dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(17,17,17,0.15)',
              }}
            >
              <div className="flex items-center gap-1.5 mb-3">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-xs ml-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  terminal
                </span>
              </div>
              <div style={{ color: '#22c55e' }}>
                <span style={{ color: 'rgba(255,255,255,0.4)' }}>$ </span>
                whoami
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)' }}>
                ritesh_v — cyber security analyst
              </div>
              <div className="mt-2" style={{ color: '#22c55e' }}>
                <span style={{ color: 'rgba(255,255,255,0.4)' }}>$ </span>
                cat status.txt
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)' }}>
                🟢 actively hunting threats & seeking internships
              </div>
              <div className="mt-2" style={{ color: '#22c55e' }}>
                <span style={{ color: 'rgba(255,255,255,0.4)' }}>$ </span>
                <span className="terminal-cursor">█</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Experience Section ───────────────────────────────────────────────────────
function ExperienceSection() {
  const { dark } = useTheme();
  const [expanded, setExpanded] = useState<number | null>(0);

  return (
    <section id="experience" className="py-24 px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-14 animate-on-scroll">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="font-mono text-xs px-3 py-1 rounded-full"
              style={{
                background: 'rgba(220,38,38,0.1)',
                color: '#DC2626',
                border: '1px solid rgba(220,38,38,0.2)',
              }}
            >
              ◆ experience
            </span>
          </div>
          <p
            className={`section-title text-3xl sm:text-4xl mb-2 ${dark ? 'section-title-dark' : ''}`}
          >
            Experience
          </p>
          <p
            className="text-xl sm:text-2xl"
            style={{
              color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
            }}
          >
            Where I&apos;ve Applied My Skills!
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div
            className="absolute left-6 top-0 bottom-0 w-px"
            style={{ background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(17,17,17,0.08)' }}
          />

          <div className="flex flex-col gap-6">
            {EXPERIENCE.map((exp, idx) => (
              <div key={idx} className="animate-on-scroll relative pl-16">
                {/* Timeline dot */}
                <div
                  className="absolute left-4 top-6 w-4 h-4 rounded-full border-2 flex items-center justify-center"
                  style={{
                    background: '#DC2626',
                    borderColor: dark ? '#0f0f0f' : '#ffffff',
                    boxShadow: '0 0 0 4px rgba(220,38,38,0.15)',
                    zIndex: 1,
                  }}
                />

                <div
                  className="experience-card-enhanced rounded-2xl overflow-hidden cursor-pointer"
                  onClick={() => setExpanded(expanded === idx ? null : idx)}
                  style={{
                    background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
                    border: dark
                      ? '1px solid rgba(255,255,255,0.08)'
                      : '1px solid rgba(17,17,17,0.08)',
                    boxShadow:
                      expanded === idx
                        ? dark
                          ? '0 8px 32px rgba(220,38,38,0.12)'
                          : '0 8px 32px rgba(220,38,38,0.08)'
                        : dark
                          ? '0 4px 16px rgba(0,0,0,0.2)'
                          : '0 4px 16px rgba(0,0,0,0.04)',
                  }}
                >
                  {/* Card Header */}
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        {/* Company Icon */}
                        <div
                          className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                          style={{
                            background: 'rgba(220,38,38,0.08)',
                            border: '1px solid rgba(220,38,38,0.15)',
                          }}
                        >
                          {exp.icon || '🏢'}
                        </div>
                        <div>
                          <h3
                            className="text-lg sm:text-xl mb-0.5 font-bold"
                            style={{
                              fontFamily: 'Outfit, sans-serif',
                              fontWeight: 700,
                              color: dark ? '#f1f1f1' : '#111111',
                              lineHeight: '1.3',
                            }}
                          >
                            {exp.title}
                          </h3>
                          <p
                            className="text-sm font-semibold"
                            style={{ color: '#DC2626', fontFamily: 'Inter, sans-serif' }}
                          >
                            {exp.company}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className="tag-pill flex-shrink-0"
                          style={{
                            background: 'rgba(220,38,38,0.10)',
                            color: '#DC2626',
                          }}
                        >
                          {exp.duration}
                        </span>
                        {/* Expand chevron */}
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300"
                          style={{
                            background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(17,17,17,0.04)',
                            transform: expanded === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ color: dark ? 'rgba(241,241,241,0.5)' : 'rgba(17,17,17,0.5)' }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Certificate link - always visible */}
                    {exp.certificateLink && (
                      <div className="mt-4">
                        <a
                          href={exp.certificateLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="certificate-link inline-flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="8" r="6" />
                            <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
                          </svg>
                          view internship certificate
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Expandable Points */}
                  <div
                    className="overflow-hidden transition-all duration-500"
                    style={{
                      maxHeight: expanded === idx ? '500px' : '0',
                      opacity: expanded === idx ? 1 : 0,
                    }}
                  >
                    <div
                      className="px-6 pb-6 pt-2"
                      style={{
                        borderTop: dark
                          ? '1px solid rgba(255,255,255,0.06)'
                          : '1px solid rgba(17,17,17,0.06)',
                      }}
                    >
                      <ul className="space-y-3 pt-4">
                        {exp.points.map((point, pIdx) => (
                          <li
                            key={pIdx}
                            className="flex items-start gap-3 text-base sm:text-lg"
                            style={{
                              color: dark ? 'rgba(241,241,241,0.70)' : 'rgba(17,17,17,0.70)',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              lineHeight: '1.5',
                            }}
                          >
                            <span
                              className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 text-xs"
                              style={{
                                background: 'rgba(220,38,38,0.1)',
                                color: '#DC2626',
                                fontFamily: 'Space Mono, monospace',
                              }}
                            >
                              {pIdx + 1}
                            </span>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Currently seeking */}
        <div
          className="mt-12 p-6 rounded-2xl animate-on-scroll"
          style={{
            background:
              'linear-gradient(135deg, rgba(220,38,38,0.06) 0%, rgba(153,50,250,0.06) 100%)',
            border: '1px solid rgba(220,38,38,0.15)',
          }}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎯</span>
            <div>
              <p
                className="font-semibold text-lg"
                style={{
                  color: dark ? '#f1f1f1' : '#111111',
                  fontFamily: 'Outfit, sans-serif',
                }}
              >
                Currently Seeking Internship Opportunities
              </p>
              <p
                className="text-sm mt-1"
                style={{
                  color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Open to SOC analyst, threat intelligence, and security engineering roles.
              </p>
            </div>
            <a
              href="mailto:v.riteshvijaykumar@gmail.com"
              className="ml-auto flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 hover:opacity-90"
              style={{
                background: '#DC2626',
                color: '#ffffff',
                fontFamily: 'Space Mono, monospace',
              }}
            >
              reach out →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Certifications Section ──────────────────────────────────────────────────
function CertPreview({ cert, hovered, dark }: { cert: Certification; hovered: boolean; dark: boolean }) {
  const [useIframe, setUseIframe] = useState(false);
  const [imgUrlIndex, setImgUrlIndex] = useState(0);

  const urls = [
    cert.image,
    `https://lh3.googleusercontent.com/d/${cert.driveFileId}=w800`,
    `https://drive.google.com/thumbnail?id=${cert.driveFileId}&sz=w800`,
  ];

  const handleImgError = () => {
    if (imgUrlIndex < urls.length - 1) {
      setImgUrlIndex(imgUrlIndex + 1);
    } else {
      setUseIframe(true);
    }
  };

  if (useIframe) {
    return (
      <div className="w-full h-full overflow-hidden relative pointer-events-none">
        <iframe
          src={`https://drive.google.com/file/d/${cert.driveFileId}/preview`}
          className="w-full h-[260px] border-0 -mt-8"
          title={`${cert.title} preview`}
          tabIndex={-1}
        />
      </div>
    );
  }

  return (
    <img
      src={urls[imgUrlIndex]}
      alt={`${cert.title} certificate preview`}
      referrerPolicy="no-referrer"
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'top',
        transform: hovered ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.5s ease',
        filter: dark ? 'brightness(0.85)' : 'none',
      }}
      onError={handleImgError}
    />
  );
}

function CertificationCard({ cert, idx }: { cert: Certification; idx: number }) {
  const { dark } = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={cert.driveLink}
      target="_blank"
      rel="noopener noreferrer"
      className="cert-card animate-on-scroll block rounded-3xl overflow-hidden"
      style={{ animationDelay: `${idx * 0.12}s`, textDecoration: 'none' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Certificate Image Preview */}
      <div
        className="relative w-full overflow-hidden"
        style={{ height: '200px', background: dark ? 'rgba(255,255,255,0.04)' : cert.bgColor }}
      >
        <CertPreview cert={cert} hovered={hovered} dark={dark} />

        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: dark
              ? 'linear-gradient(to bottom, transparent 50%, rgba(15,15,15,0.9) 100%)'
              : 'linear-gradient(to bottom, transparent 50%, rgba(255,255,255,0.95) 100%)',
          }}
        />

        {/* Hover — view badge */}
        <div
          className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
          style={{ opacity: hovered ? 1 : 0 }}
        >
          <span
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium"
            style={{
              background: 'rgba(0,0,0,0.65)',
              color: '#fff',
              fontFamily: 'Space Mono, monospace',
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            view certificate
          </span>
        </div>

        {/* Cert number badge */}
        <div className="absolute top-3 left-3">
          <span
            className="font-mono text-xs px-2.5 py-1 rounded-full"
            style={{
              background: `${cert.accentColor}22`,
              color: cert.accentColor,
              border: `1px solid ${cert.accentColor}44`,
              backdropFilter: 'blur(8px)',
            }}
          >
            0{cert.id}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div
        className="p-5"
        style={{
          background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.95)',
          borderTop: dark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(17,17,17,0.06)',
        }}
      >
        {/* Issuer row */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg">{cert.issuerIcon}</span>
          <span
            className="text-xs font-semibold"
            style={{
              color: cert.accentColor,
              fontFamily: 'Space Mono, monospace',
            }}
          >
            {cert.issuer}
          </span>
        </div>

        {/* Title */}
        <h3
          className="text-lg sm:text-xl mb-3 font-bold"
          style={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 700,
            color: dark ? '#f1f1f1' : '#111111',
            lineHeight: '1.3',
          }}
        >
          {cert.title}
        </h3>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {cert.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2.5 py-1 rounded-full"
              style={{
                background: dark ? 'rgba(255,255,255,0.06)' : cert.bgColor,
                color: dark ? 'rgba(241,241,241,0.6)' : cert.accentColor,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                border: `1px solid ${cert.accentColor}22`,
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </a>
  );
}

function CertificationsSection() {
  const { dark } = useTheme();
  return (
    <section id="certifications" className={`py-24 px-8 ${dark ? 'dot-pattern-dark' : 'dot-pattern'}`}>
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="mb-14 animate-on-scroll">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="font-mono text-xs px-3 py-1 rounded-full"
              style={{
                background: 'rgba(220,38,38,0.1)',
                color: '#DC2626',
                border: '1px solid rgba(220,38,38,0.2)',
              }}
            >
              ◆ certifications
            </span>
          </div>
          <p className={`section-title text-3xl sm:text-4xl mb-2 ${dark ? 'section-title-dark' : ''}`}>
            Certifications
          </p>
          <p
            className="text-xl sm:text-2xl"
            style={{
              color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
            }}
          >
            Credentials That Validate My Expertise!
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CERTIFICATIONS.map((cert, idx) => (
            <CertificationCard key={cert.id} cert={cert} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Achievements Section ────────────────────────────────────────────────────
function AchievementsSection() {
  const { dark } = useTheme();
  // 0 = video, 1 = cyberfest1.jpeg, 2 = cyberfest2.jpeg
  const [activeIndex, setActiveIndex] = useState(0);
  const TOTAL = 3;

  const prev = () => setActiveIndex((i) => (i - 1 + TOTAL) % TOTAL);
  const next = () => setActiveIndex((i) => (i + 1) % TOTAL);

  const mediaTitles = ['Event Highlights (Video)', 'Event Gallery — 1', 'Event Gallery — 2'];

  return (
    <section id="achievements" className="py-24 px-8">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="mb-14 animate-on-scroll">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="font-mono text-xs px-3 py-1 rounded-full"
              style={{
                background: 'rgba(220,38,38,0.1)',
                color: '#DC2626',
                border: '1px solid rgba(220,38,38,0.2)',
              }}
            >
              ◆ achievements
            </span>
          </div>
          <p className={`section-title text-3xl sm:text-4xl mb-2 ${dark ? 'section-title-dark' : ''}`}>
            Achievements
          </p>
          <p
            className="text-xl sm:text-2xl"
            style={{
              color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
            }}
          >
            Milestones Beyond the Screen!
          </p>
        </div>

        {/* Achievement Card */}
        <div
          className="animate-on-scroll rounded-3xl overflow-hidden"
          style={{
            background: dark
              ? 'linear-gradient(135deg, rgba(30,10,10,0.95) 0%, rgba(15,15,30,0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,245,255,0.98) 100%)',
            border: dark ? '1px solid rgba(220,38,38,0.2)' : '1px solid rgba(220,38,38,0.12)',
            boxShadow: dark
              ? '0 24px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(220,38,38,0.08)'
              : '0 24px 80px rgba(0,0,0,0.08), 0 0 0 1px rgba(220,38,38,0.06)',
          }}
        >
          {/* Media Carousel */}
          <div className="relative" style={{ background: '#000', height: '480px' }}>
            {/* Video Slide */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                opacity: activeIndex === 0 ? 1 : 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: activeIndex === 0 ? 'auto' : 'none',
              }}
            >
              <video
                src="/assets/videos/cyberfest.mp4"
                controls
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            </div>

            {/* Image Slide 1 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                opacity: activeIndex === 1 ? 1 : 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: activeIndex === 1 ? 'auto' : 'none',
              }}
            >
              <img
                src="/assets/images/cyberfest1.jpeg"
                alt="CyberFest'26 — Event Photo 1"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', objectPosition: 'center' }}
              />
            </div>

            {/* Image Slide 2 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                opacity: activeIndex === 2 ? 1 : 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: activeIndex === 2 ? 'auto' : 'none',
              }}
            >
              <img
                src="/assets/images/cyberfest2.jpeg"
                alt="CyberFest'26 — Event Photo 2"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', objectPosition: 'center' }}
              />
            </div>

            {/* Gradient overlay bottom */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '80px',
                background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)',
                pointerEvents: 'none',
              }}
            />

            {/* Nav Buttons */}
            <button
              id="achievement-carousel-prev"
              onClick={prev}
              aria-label="Previous media"
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.18)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                zIndex: 10,
                transition: 'background 0.2s, transform 0.2s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.75)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-50%) scale(1.08)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.55)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <button
              id="achievement-carousel-next"
              onClick={next}
              aria-label="Next media"
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.18)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                zIndex: 10,
                transition: 'background 0.2s, transform 0.2s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.75)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-50%) scale(1.08)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.55)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* Dots */}
            <div
              style={{
                position: 'absolute',
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px',
                zIndex: 10,
              }}
            >
              {[0, 1, 2].map((i) => (
                <button
                  key={i}
                  id={`achievement-dot-${i}`}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  style={{
                    width: i === activeIndex ? '24px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    background: i === activeIndex ? '#DC2626' : 'rgba(255,255,255,0.4)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </div>

            {/* Slide label */}
            <div
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '20px',
                padding: '4px 12px',
                color: 'rgba(255,255,255,0.85)',
                fontSize: '11px',
                fontFamily: 'Space Mono, monospace',
                zIndex: 10,
              }}
            >
              {activeIndex + 1} / {TOTAL} · {mediaTitles[activeIndex]}
            </div>
          </div>

          {/* Content */}
          <div className="p-8 lg:p-10">
            {/* Header row */}
            <div className="flex flex-wrap items-start gap-4 mb-6">
              <div
                className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-3xl"
                style={{
                  background: 'rgba(220,38,38,0.10)',
                  border: '1px solid rgba(220,38,38,0.2)',
                }}
              >
                🏆
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h2
                    className="font-bold"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 700,
                      fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
                      color: dark ? '#f1f1f1' : '#111111',
                      lineHeight: '1.25',
                      margin: 0,
                    }}
                  >
                    CyberFest&apos;26
                  </h2>
                  <span
                    className="font-mono text-xs px-3 py-1 rounded-full"
                    style={{
                      background: 'rgba(220,38,38,0.12)',
                      color: '#DC2626',
                      border: '1px solid rgba(220,38,38,0.25)',
                    }}
                  >
                    Core Organizer · CTF Designer
                  </span>
                </div>
                <p
                  className="text-sm font-semibold"
                  style={{ color: '#DC2626', fontFamily: 'Inter, sans-serif' }}
                >
                  Department of Cyber Security · KCG College of Technology
                </p>
              </div>
            </div>

            {/* Description */}
            <p
              className="text-base sm:text-lg leading-relaxed mb-8"
              style={{
                color: dark ? 'rgba(241,241,241,0.72)' : 'rgba(17,17,17,0.72)',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                lineHeight: '1.75',
              }}
            >
              CyberFest&apos;26 was the flagship cybersecurity technical fest organized by the
              Department of Cyber Security at KCG College of Technology, bringing together students
              from multiple institutions for hands-on cybersecurity competitions and learning. As a
              core organizing team member, I played a key role in planning and executing the event
              while managing the <strong style={{ color: dark ? '#f87171' : '#DC2626' }}>NYX-CTF</strong>,
              where I designed, deployed, and maintained challenges across domains such as{' '}
              <strong style={{ color: dark ? '#f87171' : '#DC2626' }}>
                web exploitation, reverse engineering, steganography, OSINT, and digital forensics
              </strong>. The event provided participants with a realistic, competitive environment
              to develop practical cybersecurity skills.
            </p>

            {/* Highlights grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              {[
                { icon: '🎯', label: 'CTF Designer', sub: 'NYX-CTF challenges' },
                { icon: '🌐', label: 'Web Exploitation', sub: 'Challenge domain' },
                { icon: '🔍', label: 'Reverse Engineering', sub: 'Challenge domain' },
                { icon: '🖼️', label: 'Steganography', sub: 'Challenge domain' },
                { icon: '🕵️', label: 'OSINT', sub: 'Challenge domain' },
                { icon: '🗃️', label: 'Digital Forensics', sub: 'Challenge domain' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="p-4 rounded-2xl flex flex-col gap-1"
                  style={{
                    background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(17,17,17,0.03)',
                    border: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(17,17,17,0.07)',
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{item.icon}</span>
                  <p
                    className="text-sm font-semibold"
                    style={{
                      color: dark ? '#f1f1f1' : '#111111',
                      fontFamily: 'Outfit, sans-serif',
                      margin: 0,
                    }}
                  >
                    {item.label}
                  </p>
                  <p
                    className="text-xs"
                    style={{
                      color: dark ? 'rgba(241,241,241,0.45)' : 'rgba(17,17,17,0.45)',
                      fontFamily: 'Inter, sans-serif',
                      margin: 0,
                    }}
                  >
                    {item.sub}
                  </p>
                </div>
              ))}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'CTF Design', bg: 'rgba(220,38,38,0.10)', color: '#DC2626' },
                { label: 'Event Organizing', bg: 'rgba(153,50,250,0.10)', color: '#7c3aed' },
                { label: 'Web Exploitation', bg: 'rgba(36,145,255,0.10)', color: '#1d4ed8' },
                { label: 'Reverse Engineering', bg: 'rgba(38,156,48,0.10)', color: '#166534' },
                { label: 'Steganography', bg: 'rgba(255,116,36,0.10)', color: '#c2410c' },
                { label: 'OSINT', bg: 'rgba(255,36,229,0.10)', color: '#86198f' },
                { label: 'Digital Forensics', bg: 'rgba(220,38,38,0.10)', color: '#DC2626' },
              ].map((tag) => (
                <span
                  key={tag.label}
                  className="tag-pill text-xs font-medium"
                  style={{ backgroundColor: tag.bg, color: tag.color }}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Events Section ───────────────────────────────────────────────────────────
const EVENTS = [
  {
    id: 1,
    name: 'CIT Talos',
    image: '/assets/images/events/CIT Talos.jpeg',
    driveLink: 'https://drive.google.com/file/d/1wXLRoVMmkmV-E8jiIEYYGmnKm0FOzA5x/view?usp=sharing',
    tag: 'Technical Fest',
    color: '#DC2626',
    emoji: '⚡',
  },
  {
    id: 2,
    name: 'IET Project Summit',
    image: '/assets/images/events/IET projecct summit.jpeg',
    driveLink: 'https://drive.google.com/file/d/1LIBs3AJ0D2ZldZDbwWiMir4fuuLnzcBS/view?usp=sharing',
    tag: 'Project Summit',
    color: '#1d4ed8',
    emoji: '🚀',
  },
  {
    id: 3,
    name: "Innothon'24 Hackathon",
    image: "/assets/images/events/innothon'24 hackton.jpeg",
    driveLink: 'https://drive.google.com/file/d/1ybzlZXFg56S13ciIUwv8yajnZp3Gl-qQ/view?usp=sharing',
    tag: 'Hackathon',
    color: '#7c3aed',
    emoji: '💡',
  },
  {
    id: 4,
    name: "Innothon'25 Hackathon",
    image: "/assets/images/events/innothon'25 hackaton.jpeg",
    driveLink: 'https://drive.google.com/file/d/14s2qMIiTxgjhNt215_8LRMmZAe874EiK/view?usp=sharing',
    tag: 'Hackathon',
    color: '#c2410c',
    emoji: '🔥',
  },
  {
    id: 5,
    name: 'Spyirex CTF',
    image: '/assets/images/events/Spyirex ctf.jpeg',
    driveLink: 'https://drive.google.com/file/d/194L1p-Gm4QEfvQRofeepnTiXlgcmMPln/view?usp=sharing',
    tag: 'CTF Competition',
    color: '#166534',
    emoji: '🕵️',
  },
  {
    id: 6,
    name: 'Zero Day CTF',
    image: '/assets/images/events/zero day CTF.jpeg',
    driveLink: 'https://drive.google.com/file/d/1qvfsqqVRaHmzSSIoc_PQBdgoWTzj099S/view?usp=sharing',
    tag: 'CTF Competition',
    color: '#86198f',
    emoji: '🔓',
  },
];

function EventsSection() {
  const { dark } = useTheme();
  const [paused, setPaused] = useState(false);
  // Duplicate the array for seamless infinite scroll
  const items = [...EVENTS, ...EVENTS];

  return (
    <section
      id="events"
      className="py-20 px-0 overflow-hidden"
      style={{
        background: dark
          ? 'linear-gradient(180deg, #0a0a12 0%, #0d0d1a 100%)'
          : 'linear-gradient(180deg, #f8f8fc 0%, #f2f2f8 100%)',
        borderTop: dark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(17,17,17,0.07)',
        borderBottom: dark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(17,17,17,0.07)',
      }}
    >
      {/* Section header */}
      <div className="max-w-5xl mx-auto px-8 mb-12 animate-on-scroll">
        <div className="flex items-center gap-3 mb-3">
          <span
            className="font-mono text-xs px-3 py-1 rounded-full"
            style={{
              background: 'rgba(220,38,38,0.1)',
              color: '#DC2626',
              border: '1px solid rgba(220,38,38,0.2)',
            }}
          >
            ◆ events &amp; participations
          </span>
        </div>
        <h2
          className="section-title"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3.2rem)',
            color: dark ? '#f1f1f1' : '#111111',
          }}
        >
          competitions &amp;{' '}
          <span style={{ color: '#DC2626', fontFamily: 'Outfit, sans-serif', fontStyle: 'italic', fontWeight: 500 }}>
            events
          </span>
        </h2>
        <p
          className="text-base mt-3 max-w-xl"
          style={{
            color: dark ? 'rgba(241,241,241,0.55)' : 'rgba(17,17,17,0.55)',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
          }}
        >
          Certificates from hackathons, CTFs, and domain-specific technical events I&apos;ve participated in.
        </p>
      </div>

      {/* Scrolling strip */}
      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{ overflow: 'hidden' }}
      >
        {/* Fade edges */}
        <div
          className="absolute left-0 top-0 bottom-0 z-10 pointer-events-none"
          style={{
            width: '120px',
            background: dark
              ? 'linear-gradient(to right, #0a0a12, transparent)'
              : 'linear-gradient(to right, #f8f8fc, transparent)',
          }}
        />
        <div
          className="absolute right-0 top-0 bottom-0 z-10 pointer-events-none"
          style={{
            width: '120px',
            background: dark
              ? 'linear-gradient(to left, #0a0a12, transparent)'
              : 'linear-gradient(to left, #f8f8fc, transparent)',
          }}
        />

        {/* Scrolling row */}
        <div
          className="events-scroll-track"
          style={{
            display: 'flex',
            gap: '24px',
            paddingLeft: '24px',
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {items.map((event, idx) => (
            <a
              key={`${event.id}-${idx}`}
              href={event.driveLink}
              target="_blank"
              rel="noopener noreferrer"
              className="event-cert-card"
              aria-label={`View ${event.name} certificate`}
              style={{
                flexShrink: 0,
                width: '280px',
                borderRadius: '20px',
                overflow: 'hidden',
                display: 'block',
                textDecoration: 'none',
                background: dark ? 'rgba(255,255,255,0.04)' : '#ffffff',
                border: dark
                  ? '1px solid rgba(255,255,255,0.08)'
                  : '1px solid rgba(17,17,17,0.07)',
                boxShadow: dark
                  ? '0 8px 32px rgba(0,0,0,0.35)'
                  : '0 4px 24px rgba(0,0,0,0.07)',
                transition: 'transform 0.35s cubic-bezier(0.16,1,0.3,1), box-shadow 0.35s ease, border-color 0.3s ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-10px) scale(1.03)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = dark
                  ? `0 20px 48px rgba(0,0,0,0.5), 0 0 0 1.5px ${event.color}55`
                  : `0 20px 48px rgba(0,0,0,0.14), 0 0 0 1.5px ${event.color}44`;
                (e.currentTarget as HTMLAnchorElement).style.borderColor = `${event.color}66`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0) scale(1)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = dark
                  ? '0 8px 32px rgba(0,0,0,0.35)'
                  : '0 4px 24px rgba(0,0,0,0.07)';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = dark
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(17,17,17,0.07)';
              }}
            >
              {/* Certificate image */}
              <div style={{ position: 'relative', height: '190px', overflow: 'hidden', background: dark ? 'rgba(255,255,255,0.03)' : '#f5f5f8' }}>
                <Image
                  src={event.image}
                  alt={`${event.name} certificate`}
                  width={280}
                  height={190}
                  className="w-full h-full object-cover"
                  style={{ display: 'block' }}
                />
                {/* Gradient overlay */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to bottom, transparent 55%, rgba(0,0,0,0.62) 100%)',
                  }}
                />
                {/* Emoji badge */}
                <div
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                  }}
                >
                  {event.emoji}
                </div>
                {/* View badge */}
                <div
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: `${event.color}22`,
                    border: `1px solid ${event.color}44`,
                    backdropFilter: 'blur(8px)',
                    borderRadius: '20px',
                    padding: '3px 10px',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontFamily: 'Space Mono, monospace',
                  }}
                >
                  view cert ↗
                </div>
              </div>

              {/* Card info */}
              <div style={{ padding: '14px 16px 16px' }}>
                <p
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: dark ? '#f1f1f1' : '#111111',
                    margin: '0 0 6px',
                    lineHeight: 1.3,
                  }}
                >
                  {event.name}
                </p>
                <span
                  style={{
                    display: 'inline-block',
                    fontFamily: 'Space Mono, monospace',
                    fontSize: '10px',
                    padding: '3px 10px',
                    borderRadius: '20px',
                    background: `${event.color}18`,
                    color: event.color,
                    border: `1px solid ${event.color}33`,
                    fontWeight: 500,
                  }}
                >
                  {event.tag}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Connect / Footer Section ─────────────────────────────────────────────────
function ConnectSection() {
  const [emailCopied, setEmailCopied] = useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText('v.riteshvijaykumar@gmail.com');
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  const connectLinks = [
    {
      label: 'LinkedIn',
      href: 'https://linkedin.com/in/ritesh-v-/',
      icon: <LinkedInIcon size={24} />,
      desc: 'Connect professionally',
    },
    {
      label: 'GitHub',
      href: 'https://github.com/riteshvijaykumar',
      icon: <GitHubIcon size={24} />,
      desc: 'View my code',
    },
    {
      label: 'Email',
      href: 'mailto:v.riteshvijaykumar@gmail.com',
      icon: <EmailIcon size={24} />,
      desc: 'Send a message',
    },
  ];

  return (
    <section
      id="contact"
      className="relative py-28 px-8 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #533483 100%)',
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-0 right-0 bottom-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #dc2626, transparent)' }}
        />
        <div
          className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #7c3aed, transparent)' }}
        />
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-14">
          <p
            className="text-xs font-mono mb-4 tracking-widest opacity-60"
            style={{ color: '#ffffff', letterSpacing: '0.2em' }}
          >
            ◆ GET IN TOUCH
          </p>
          <p
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: 'clamp(32px, 5vw, 56px)',
              color: '#ffffff',
              lineHeight: '1.2',
            }}
          >
            Let&apos;s connect!
          </p>
          <p
            className="mt-4 text-lg max-w-lg mx-auto"
            style={{
              color: 'rgba(255,255,255,0.75)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
            }}
          >
            Thank you for taking time to visit my portfolio. I would love to talk security, projects,
            or internship opportunities!
          </p>
        </div>

        {/* Connect Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-12">
          {connectLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target={link.href.startsWith('mailto') ? undefined : '_blank'}
              rel="noopener noreferrer"
              className="connect-card-enhanced group"
            >
              <div
                className="p-6 rounded-2xl text-center transition-all duration-300 group-hover:-translate-y-2"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{
                    background: 'rgba(220,38,38,0.15)',
                    border: '1.5px solid rgba(220,38,38,0.3)',
                  }}
                >
                  {link.icon}
                </div>
                <p className="font-mono text-sm font-medium text-white mb-1">{link.label}</p>
                <p
                  className="text-xs"
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {link.desc}
                </p>
              </div>
            </a>
          ))}
        </div>

        {/* Copy Email */}
        <div className="text-center">
          <button
            onClick={copyEmail}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300"
            style={{
              background: emailCopied ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.1)',
              border: `1.5px solid ${emailCopied ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.2)'}`,
              color: emailCopied ? '#22c55e' : 'rgba(255,255,255,0.75)',
              fontFamily: 'Space Mono, monospace',
              fontSize: '13px',
              cursor: 'pointer',
              backdropFilter: 'blur(8px)',
            }}
          >
            {emailCopied ? (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                copied!
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                v.riteshvijaykumar@gmail.com
              </>
            )}
          </button>
        </div>

        {/* Footer Bar */}
        <div
          className="relative mt-16 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}
        >
          <p
            className="text-sm"
            style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}
          >
            © 2026 Ritesh V | Made with ❤️ &amp; security focus
          </p>
          <div className="flex items-center gap-1">
            <span
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ background: '#DC2626' }}
            />
            <span
              className="text-xs"
              style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Space Mono, monospace' }}
            >
              cyber security analyst
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────
function LinkedInIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"
        stroke="#DC2626"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="2"
        y="9"
        width="4"
        height="12"
        stroke="#DC2626"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="4" cy="4" r="2" stroke="#DC2626" strokeWidth="1.5" />
    </svg>
  );
}

function GitHubIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"
        stroke="#DC2626"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EmailIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
        stroke="#DC2626"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="22,6 12,13 2,6"
        stroke="#DC2626"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PortfolioPage() {
  useScrollAnimation();
  const [dark, setDark] = useState(false);
  const [nameHovered, setNameHovered] = useState(false);

  const toggle = () => setDark((prev) => !prev);

  return (
    <ThemeContext.Provider value={{ dark, toggle, nameHovered, setNameHovered }}>
      <main
        className="min-h-screen transition-colors duration-500"
        style={{
          backgroundColor: dark ? '#0a0a12' : '#ffffff',
          color: dark ? '#f0f0f5' : '#111111',
        }}
      >
        <Nav />
        <HeroSection />
        <div
          style={{
            opacity: nameHovered ? 0.25 : 1,
            transition: 'opacity 0.4s ease',
          }}
        >
          <ProjectsSection />
          <AboutSection />
          <ExperienceSection />
          <CertificationsSection />
          <AchievementsSection />
          <EventsSection />
          <ConnectSection />
        </div>
      </main>
    </ThemeContext.Provider>
  );
}
